(() => {
  const buttonSelector = [
    'a.button:not(.button--tertiary)',
    'button.button:not(.button--tertiary)',
    '.shopify-challenge__button',
    '.customer button',
    'button.shopify-payment-button__button--unbranded',
    'a.mission-button',
    'a.ws-button',
    'a.wood-home-grid__button',
    'a.wood-mission__btn',
    'a.ws-final-cta-button',
    'a.ws-protein-hero__btn',
    'a.ws-protein-final-cta__button',
    'a.ws-protein-flavors__button',
    'button.ws-protein-calculator__button',
    'a.ws-qqc-commitment__button',
  ].join(',');

  const excludedSelector = [
    '.skip-to-content-link',
    '.product__xr-button',
    '.header__icon',
    '.slider-button',
    '.quantity__button',
    '.field__button',
    '.modal__close-button',
    '.menu-drawer__close-button',
    '.mobile-facets__close-button',
    '.deferred-media__poster-button',
    '.pickup-availability-button',
    '.pickup-availability-drawer-button',
    '.product-popup-modal__button',
    '.localization-form__select',
    '[class*="remove-button"]',
    '[class*="close-button"]',
  ].join(',');

  const normalizeLabel = (value) => value.replace(/\s+/g, ' ').trim();

  const parseColor = (value) => {
    const channels = value.match(/[\d.]+/g)?.map(Number);
    if (!channels || channels.length < 3) return null;
    return { red: channels[0], green: channels[1], blue: channels[2], alpha: channels[3] ?? 1 };
  };

  const opaqueSurface = (element) => {
    let current = element;

    while (current) {
      const color = getComputedStyle(current).backgroundColor;
      const parsed = parseColor(color);
      if (parsed && parsed.alpha > 0.15) return `rgb(${parsed.red}, ${parsed.green}, ${parsed.blue})`;
      current = current.parentElement;
    }

    return 'rgb(255, 255, 255)';
  };

  const findDefaultLabel = (button) => {
    const existing = button.querySelector(':scope > .wood-button-motion__label-default');
    if (existing) return existing;

    const directTextNodes = Array.from(button.childNodes).filter(
      (node) => node.nodeType === Node.TEXT_NODE && normalizeLabel(node.textContent)
    );

    if (directTextNodes.length) {
      const wrapper = document.createElement('span');
      wrapper.className = 'wood-button-motion__label-default';
      button.insertBefore(wrapper, directTextNodes[0]);
      directTextNodes.forEach((node) => wrapper.appendChild(node));
      return wrapper;
    }

    const candidate = Array.from(button.children).find(
      (child) =>
        !child.matches('.loading__spinner, svg, .icon, .wood-button-motion__label-hover') &&
        normalizeLabel(child.textContent)
    );

    if (!candidate) return null;
    candidate.classList.add('wood-button-motion__label-default');
    return candidate;
  };

  const syncButton = (button) => {
    if (!(button instanceof HTMLElement) || button.matches(excludedSelector)) return;

    const isFirstEnhancement = !button.hasAttribute('data-wood-button-motion');
    if (isFirstEnhancement) button.setAttribute('data-wood-button-motion-initializing', '');

    const defaultLabel = findDefaultLabel(button);
    const label = normalizeLabel(defaultLabel?.textContent || '');
    if (!defaultLabel || !label) {
      button.removeAttribute('data-wood-button-motion-initializing');
      return;
    }

    let hoverLabel = button.querySelector(':scope > .wood-button-motion__label-hover');
    if (!hoverLabel) {
      hoverLabel = document.createElement('span');
      hoverLabel.className = 'wood-button-motion__label-hover';
      hoverLabel.setAttribute('aria-hidden', 'true');
      button.appendChild(hoverLabel);
    }

    if (hoverLabel.textContent !== label) hoverLabel.textContent = label;

    const styles = getComputedStyle(button);
    button.style.setProperty('--wood-button-sweep-background', styles.color);
    button.style.setProperty('--wood-button-sweep-color', opaqueSurface(button));
    button.setAttribute('data-wood-button-motion', '');

    if (isFirstEnhancement) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => button.removeAttribute('data-wood-button-motion-initializing'));
      });
    }
  };

  const enhanceWithin = (root) => {
    if (!(root instanceof Element || root instanceof Document)) return;
    if (root instanceof Element && root.matches(buttonSelector)) syncButton(root);
    root.querySelectorAll(buttonSelector).forEach(syncButton);
  };

  const refreshClosestButton = (node) => {
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    const button = element?.closest(buttonSelector);
    if (button && !element?.closest('.wood-button-motion__label-hover')) syncButton(button);
  };

  const start = () => {
    enhanceWithin(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        refreshClosestButton(mutation.target);
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) enhanceWithin(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
    document.addEventListener('shopify:section:load', (event) => enhanceWithin(event.target));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
